import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Tag,
  Space,
  Select,
  Input,
  DatePicker,
  Button,
  Row,
  Col,
  Typography,
  Tooltip,
  Badge,
  Modal,
  Timeline,
  Descriptions,
  Statistic,
  Spin,
  Empty,
  message,
  Divider,
} from 'antd';
import {
  AuditOutlined,
  SearchOutlined,
  ReloadOutlined,
  UserOutlined,
  ClockCircleOutlined,
  SafetyOutlined,
  EyeOutlined,
  HistoryOutlined,
  TeamOutlined,
  SwapOutlined,
  PlusCircleOutlined,
  StopOutlined,
  CheckCircleOutlined,
  EditOutlined,
  GlobalOutlined,
  LaptopOutlined,
  FieldTimeOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import axiosConfig from '../../../../../config/axiosConfig';

// ── Skeleton shimmer constants ────────────────────────────────────────────────
const SKEL_GRAD = 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)';
const SKEL_GRAD_DARK = 'linear-gradient(90deg, #e0e0e0 25%, #d0d0d0 50%, #e0e0e0 75%)';
const SKEL_ANIM = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ? 'none'
  : 'skeleton-loading 1s infinite ease-in-out';

// Stat cards skeleton (6 cards matching the real layout)
const StatCardsSkeleton = React.memo(() => (
  <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <Col xs={12} sm={8} md={6} lg={4} key={i}>
        <Card size="small" style={{ borderRadius: 10, borderLeft: '3px solid #e0e0e0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }} bodyStyle={{ padding: '12px 16px' }}>
          <div style={{ height: 14, background: SKEL_GRAD, borderRadius: 4, width: '60%', marginBottom: 8, animation: SKEL_ANIM }} />
          <div style={{ height: 24, background: SKEL_GRAD_DARK, borderRadius: 4, width: '80%', animation: SKEL_ANIM }} />
        </Card>
      </Col>
    ))}
  </Row>
));

// Logs table skeleton
const LogsTableSkeleton = React.memo(() => (
  <div>
    <div style={{ border: '1px solid #f0f0f0', borderRadius: 8 }}>
      <div style={{ padding: '10px 16px', background: '#fafafa', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 12 }}>
        {[60, 80, 100, 120, 80, 60].map((w, i) => (
          <div key={i} style={{ height: 14, background: SKEL_GRAD_DARK, borderRadius: 4, width: w, flexShrink: 0, animation: SKEL_ANIM }} />
        ))}
      </div>
      {[0, 1, 2, 3, 4, 5, 6].map((r) => (
        <div key={r} style={{ padding: '11px 16px', borderBottom: r < 6 ? '1px solid #f0f0f0' : 'none', display: 'flex', gap: 12, alignItems: 'center' }}>
          {[60, 80, 100, 120, 80, 60].map((w, c) => (
            <div key={c} style={{ height: c % 2 === 0 ? 20 : 14, background: SKEL_GRAD, borderRadius: 4, width: w, flexShrink: 0, animation: SKEL_ANIM }} />
          ))}
        </div>
      ))}
    </div>
    <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 4px' }}>
      <div style={{ height: 20, background: SKEL_GRAD, borderRadius: 4, width: 160, animation: SKEL_ANIM }} />
      <div style={{ height: 28, background: SKEL_GRAD, borderRadius: 6, width: 200, animation: SKEL_ANIM }} />
    </div>
  </div>
));

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

// ─── Color map for action types ──────────────────────────────────────────────
const ACTION_CONFIG = {
  USER_CREATED:        { color: 'green',  icon: <PlusCircleOutlined />,  label: 'User Created' },
  USER_DEACTIVATED:    { color: 'red',    icon: <StopOutlined />,        label: 'User Deactivated' },
  USER_REACTIVATED:    { color: 'blue',   icon: <CheckCircleOutlined />, label: 'User Reactivated' },
  ROLE_ASSIGNED:       { color: 'purple', icon: <SafetyOutlined />,      label: 'Role Assigned' },
  ROLE_CHANGED:        { color: 'orange', icon: <SwapOutlined />,        label: 'Role Changed' },
  PERMISSIONS_UPDATED: { color: 'cyan',   icon: <EditOutlined />,        label: 'Permissions Updated' },
};

const TAG_COLOR_HEX = {
  green: '#52c41a', red: '#ff4d4f', blue: '#1890ff',
  purple: '#722ed1', orange: '#fa8c16', cyan: '#13c2c2', default: '#8c8c8c',
};

const getActionConfig = (type) =>
  ACTION_CONFIG[type] || { color: 'default', icon: <AuditOutlined />, label: type || 'Unknown' };

const ClientAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0, totalPages: 0 });

  // Filters
  const [actionTypeFilter, setActionTypeFilter] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState(null);

  // Action types dropdown
  const [actionTypes, setActionTypes] = useState([]);
  const [actionTypesLoading, setActionTypesLoading] = useState(false);

  // Stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Modals
  const [detailModal, setDetailModal] = useState({ visible: false, log: null });
  const [userHistoryModal, setUserHistoryModal] = useState({ visible: false, data: [], loading: false, name: '' });
  const [adminActionsModal, setAdminActionsModal] = useState({ visible: false, data: [], loading: false, name: '' });
  const [roleHistoryModal, setRoleHistoryModal] = useState({ visible: false, data: [], loading: false, name: '' });

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Fetch action types ───────────────────────────────────────────────────────
  const fetchActionTypes = useCallback(async () => {
    setActionTypesLoading(true);
    try {
      const res = await axiosConfig.get('/admin/client/audit-logs/action-types');
      const entity = res.data?.entity || res.data?.data;
      const isSuccess = res.data?.status === true || res.data?.statuscode === 200 || res.data?.statuscode === 201;
      if (isSuccess && Array.isArray(entity)) {
        setActionTypes(entity);
      }
    } catch {
      setActionTypes(
        Object.entries(ACTION_CONFIG).map(([key, cfg]) => ({ key, label: cfg.label, description: '' }))
      );
    } finally {
      setActionTypesLoading(false);
    }
  }, []);

  // ── Fetch statistics ─────────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await axiosConfig.get('/admin/client/audit-logs/stats', { params: { days: 30 } });
      const isSuccess = res.data?.status === true || res.data?.statuscode === 200 || res.data?.statuscode === 201;
      if (isSuccess) {
        setStats(res.data?.entity || res.data?.data);
      }
    } catch {
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // ── Fetch logs ───────────────────────────────────────────────────────────────
  const fetchLogs = useCallback(async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const body = { page, pageSize };
      if (actionTypeFilter) body.actionType = actionTypeFilter;
      if (searchText.trim()) body.search = searchText.trim();
      if (dateRange && dateRange[0] && dateRange[1]) {
        body.startDate = dateRange[0].toISOString();
        body.endDate = dateRange[1].toISOString();
      }

      const res = await axiosConfig.post('/admin/client/audit-logs', body);
      const isSuccess = res.data?.status === true || res.data?.statuscode === 200 || res.data?.statuscode === 201;
      if (isSuccess) {
        const entity = res.data?.entity || res.data?.data || {};
        setLogs(entity.logs || []);
        const pag = entity.pagination || {};
        setPagination({
          page: pag.page || page,
          pageSize: pag.pageSize || pageSize,
          total: pag.total || 0,
          totalPages: pag.totalPages || 0,
        });
      }
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
      message.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [actionTypeFilter, searchText, dateRange]);

  // ── Init ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchActionTypes();
    fetchStats();
  }, [fetchActionTypes, fetchStats]);

  useEffect(() => {
    fetchLogs(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionTypeFilter, dateRange]);

  // ── User history ─────────────────────────────────────────────────────────────
  const fetchUserHistory = async (companyId, name) => {
    setUserHistoryModal({ visible: true, data: [], loading: true, name });
    try {
      const res = await axiosConfig.get(`/admin/client/audit-logs/user/${companyId}`, { params: { limit: 50 } });
      const isSuccess = res.data?.status === true || res.data?.statuscode === 200 || res.data?.statuscode === 201;
      if (isSuccess) {
        const entity = res.data?.entity || res.data?.data;
        const uhData = entity?.logs || entity || [];
        setUserHistoryModal(prev => ({ ...prev, data: Array.isArray(uhData) ? uhData : [], loading: false }));
      } else {
        setUserHistoryModal(prev => ({ ...prev, loading: false }));
      }
    } catch {
      message.error('Failed to load user history');
      setUserHistoryModal(prev => ({ ...prev, loading: false }));
    }
  };

  // ── Admin actions (by an admin) ──────────────────────────────────────────────
  const fetchAdminActions = async (companyId, name) => {
    setAdminActionsModal({ visible: true, data: [], loading: true, name });
    try {
      const res = await axiosConfig.get(`/admin/client/audit-logs/user/${companyId}`, { params: { limit: 50 } });
      const isSuccess = res.data?.status === true || res.data?.statuscode === 200 || res.data?.statuscode === 201;
      if (isSuccess) {
        const entity = res.data?.entity || res.data?.data;
        const aaData = entity?.logs || entity || [];
        setAdminActionsModal(prev => ({ ...prev, data: Array.isArray(aaData) ? aaData : [], loading: false }));
      } else {
        setAdminActionsModal(prev => ({ ...prev, loading: false }));
      }
    } catch {
      message.error('Failed to load admin actions');
      setAdminActionsModal(prev => ({ ...prev, loading: false }));
    }
  };

  // ── Role change history ──────────────────────────────────────────────────────
  const fetchRoleHistory = async (companyId, name) => {
    setRoleHistoryModal({ visible: true, data: [], loading: true, name });
    try {
      const res = await axiosConfig.get(`/admin/client/audit-logs/user/${companyId}`, { params: { limit: 50 } });
      const isSuccess = res.data?.status === true || res.data?.statuscode === 200 || res.data?.statuscode === 201;
      if (isSuccess) {
        const entity = res.data?.entity || res.data?.data;
        const rhData = entity?.logs || entity || [];
        // Filter only role-related events
        const roleEvents = (Array.isArray(rhData) ? rhData : []).filter(
          l => l.actionType === 'ROLE_ASSIGNED' || l.actionType === 'ROLE_CHANGED'
        );
        setRoleHistoryModal(prev => ({ ...prev, data: roleEvents, loading: false }));
      } else {
        setRoleHistoryModal(prev => ({ ...prev, loading: false }));
      }
    } catch {
      message.error('Failed to load role history');
      setRoleHistoryModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleSearch = () => fetchLogs(1, pagination.pageSize);

  const handleClearFilters = () => {
    setActionTypeFilter(null);
    setSearchText('');
    setDateRange(null);
  };

  const handleRefresh = () => {
    fetchLogs(pagination.page, pagination.pageSize);
    fetchStats();
  };

  // ── Table columns ────────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Date & Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      defaultSortOrder: 'descend',
      render: (val) => {
        const d = new Date(val);
        return (
          <Tooltip title={d.toLocaleString()}>
            <Space size={4} direction="vertical" style={{ gap: 0 }}>
              <Space size={4}>
                <ClockCircleOutlined style={{ color: '#8c8c8c', fontSize: 11 }} />
                <Text style={{ fontSize: 12, fontWeight: 500 }}>{d.toLocaleDateString()}</Text>
              </Space>
              <Text type="secondary" style={{ fontSize: 11, paddingLeft: 16 }}>{d.toLocaleTimeString()}</Text>
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'Action',
      dataIndex: 'actionType',
      key: 'actionType',
      width: 190,
      filters: actionTypes.map(t => ({ text: t.label, value: t.key })),
      onFilter: (value, record) => record.actionType === value,
      render: (type, record) => {
        const cfg = getActionConfig(type);
        const label = record.actionLabel || cfg.label;
        return <Tag icon={cfg.icon} color={cfg.color} style={{ fontSize: 12, whiteSpace: 'normal' }}>{label}</Tag>;
      },
    },
    {
      title: 'Performed By',
      key: 'performedBy',
      width: 180,
      render: (_, record) => {
        const actor = record.performedBy;
        if (!actor) return <Text type="secondary">System</Text>;
        return (
          <Space size={4}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%',
              background: 'linear-gradient(135deg, #1890ff, #722ed1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 11, fontWeight: 600,
            }}>
              {(actor.name || '?').charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ fontWeight: 500, fontSize: 13, lineHeight: '18px' }}>{actor.name || 'Unknown'}</div>
              <Text type="secondary" style={{ fontSize: 11, cursor: 'pointer' }}
                onClick={() => fetchAdminActions(actor.companyId, actor.name)}
              >
                View actions →
              </Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Target User',
      key: 'target',
      width: 180,
      render: (_, record) => {
        const target = record.target;
        if (!target) return <Text type="secondary">N/A</Text>;
        return (
          <Space size={4}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%',
              background: 'linear-gradient(135deg, #52c41a, #13c2c2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 11, fontWeight: 600,
            }}>
              {(target.name || '?').charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ fontWeight: 500, fontSize: 13, lineHeight: '18px' }}>{target.name || 'Unknown'}</div>
              <Text type="secondary" style={{ fontSize: 11, cursor: 'pointer' }}
                onClick={() => fetchUserHistory(target.companyId, target.name)}
              >
                View history →
              </Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Description',
      dataIndex: 'actionDescription',
      key: 'actionDescription',
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}><Text style={{ fontSize: 13 }}>{text}</Text></Tooltip>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: windowWidth >= 768 ? false : 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button type="text" size="small" icon={<EyeOutlined />}
              onClick={() => setDetailModal({ visible: true, log: record })}
            />
          </Tooltip>
          {record.target?.companyId && (
            <Tooltip title="Role History">
              <Button type="text" size="small"
                icon={<HistoryOutlined style={{ color: '#722ed1' }} />}
                onClick={() => fetchRoleHistory(record.target.companyId, record.target.name)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // ── Stat cards ───────────────────────────────────────────────────────────────
  const renderStatCards = () => {
    if (!stats) return null;
    const { byActionType = {}, totals = {} } = stats;
    const statCards = [
      { title: 'Total Logs', value: totals.all || 0, color: '#1890ff', icon: <AuditOutlined /> },
      { title: 'Last 24 Hours', value: totals.last24Hours || 0, color: '#52c41a', icon: <ClockCircleOutlined /> },
      { title: 'Last 7 Days', value: totals.last7Days || 0, color: '#0036b0', icon: <FieldTimeOutlined /> },
    ];
    const actionEntries = Object.entries(byActionType).sort((a, b) => b[1] - a[1]).slice(0, 3);

    return (
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {statCards.map((s, i) => (
          <Col xs={12} sm={8} md={6} lg={4} key={i}>
            <Card size="small" style={{ borderRadius: 10, borderLeft: `3px solid ${s.color}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }} bodyStyle={{ padding: '12px 16px' }}>
              <Statistic
                title={<Text style={{ fontSize: 12 }}>{s.title}</Text>}
                value={s.value}
                prefix={s.icon}
                valueStyle={{ fontSize: 20, color: s.color }}
              />
            </Card>
          </Col>
        ))}
        {actionEntries.map(([key, count], i) => {
          const cfg = getActionConfig(key);
          const hex = TAG_COLOR_HEX[cfg.color] || TAG_COLOR_HEX.default;
          return (
            <Col xs={12} sm={8} md={6} lg={4} key={`a-${i}`}>
              <Card size="small" style={{ borderRadius: 10, borderLeft: `3px solid ${hex}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }} bodyStyle={{ padding: '12px 16px' }}>
                <Statistic
                  title={<Text style={{ fontSize: 12 }}>{cfg.label}</Text>}
                  value={count}
                  prefix={cfg.icon}
                  valueStyle={{ fontSize: 20, color: hex }}
                />
              </Card>
            </Col>
          );
        })}
      </Row>
    );
  };

  // ── Value block renderer ─────────────────────────────────────────────────────
  const renderValueBlock = (obj, isNew) => {
    if (!obj) return null;
    if (typeof obj === 'string') return <span style={{ fontSize: 12 }}>{obj}</span>;

    const bgStyle = isNew
      ? { background: '#f6ffed', border: '1px solid #b7eb8f' }
      : { background: '#fff1f0', border: '1px solid #ffa39e' };

    const {
      companyid: _cid, roleid: _rid,
      companyname, rolename, company_type,
      pages, permissions,
      username, mobileno, emailid, countrycode, bookid: _bid,
      ...rest
    } = obj;

    const isContactEntry = (mobileno || username) && !pages;

    return (
      <div style={{ ...bgStyle, borderRadius: 6, padding: '8px 12px', fontSize: 12, marginTop: 4 }}>
        {isContactEntry && (
          <>
            {username && <div><Text type="secondary">Name: </Text><strong>{username}</strong></div>}
            {mobileno && <div style={{ marginTop: 2 }}><Text type="secondary">Mobile: </Text><span>{countrycode ? `+${countrycode} ` : ''}{mobileno}</span></div>}
            {emailid && <div style={{ marginTop: 2 }}><Text type="secondary">Email: </Text><span>{emailid}</span></div>}
          </>
        )}
        {!isContactEntry && (
          <>
            {companyname && <div><Text type="secondary">Name: </Text><strong>{companyname}</strong></div>}
            {(rolename || company_type) && (
              <div style={{ marginTop: 2 }}>
                <Text type="secondary">Role: </Text>
                <Tag color={isNew ? 'blue' : 'default'} style={{ fontSize: 11, marginLeft: 2 }}>{rolename || company_type}</Tag>
              </div>
            )}
            {emailid && <div style={{ marginTop: 2 }}><Text type="secondary">Email: </Text><span>{emailid ?? '—'}</span></div>}
            {Array.isArray(pages) && pages.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary">Pages: </Text>
                {pages.map(p => <Tag key={p} color={isNew ? 'geekblue' : 'default'} style={{ fontSize: 11, marginRight: 2 }}>{p}</Tag>)}
              </div>
            )}
            {permissions && typeof permissions === 'object' && Object.keys(permissions).length > 0 && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary">Permissions: </Text>
                {Object.entries(permissions).map(([page, perm]) => (
                  <Tag key={page}
                    color={perm === 'write' ? (isNew ? 'green' : 'default') : (isNew ? 'orange' : 'default')}
                    style={{ fontSize: 11, marginRight: 2, marginTop: 2 }}
                  >
                    {page}: {perm}
                  </Tag>
                ))}
              </div>
            )}
          </>
        )}
        {Object.entries(rest).map(([k, v]) => v != null ? (
          <div key={k} style={{ marginTop: 2 }}>
            <Text type="secondary">{k}: </Text>
            <span>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
          </div>
        ) : null)}
      </div>
    );
  };

  const renderChangeDiff = (oldVal, newVal) => {
    if (!oldVal && !newVal) return <Text type="secondary" style={{ fontSize: 13 }}>No change data recorded.</Text>;
    return (
      <div>
        {oldVal && (
          <div style={{ marginBottom: 10 }}>
            <Text strong style={{ fontSize: 12 }}>⬅ Before</Text>
            {renderValueBlock(oldVal, false)}
          </div>
        )}
        {newVal && (
          <div>
            <Text strong style={{ fontSize: 12 }}>➡ After</Text>
            {renderValueBlock(newVal, true)}
          </div>
        )}
      </div>
    );
  };

  // ── Timeline renderer ────────────────────────────────────────────────────────
  const renderTimeline = (data) => {
    if (!data || data.length === 0) return <Empty description="No records found" />;
    return (
      <Timeline style={{ marginTop: 16 }}>
        {data.map((item, i) => {
          const cfg = getActionConfig(item.actionType);
          return (
            <Timeline.Item
              key={item.auditId || i}
              color={cfg.color === 'green' ? 'green' : cfg.color === 'red' ? 'red' : cfg.color === 'blue' ? 'blue' : 'gray'}
              dot={cfg.icon}
            >
              <div style={{ marginBottom: 4 }}>
                <Tag color={cfg.color} style={{ fontSize: 11 }}>{item.actionLabel || cfg.label}</Tag>
                <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>{new Date(item.createdAt).toLocaleString()}</Text>
              </div>
              <Text style={{ fontSize: 13 }}>{item.actionDescription}</Text>
              {item.performedBy && (
                <div style={{ marginTop: 2 }}>
                  <Text type="secondary" style={{ fontSize: 11 }}>By: {item.performedBy.name || 'System'}</Text>
                </div>
              )}
              {item.target && (
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>On: {item.target.name || 'Unknown'}</Text>
                </div>
              )}
            </Timeline.Item>
          );
        })}
      </Timeline>
    );
  };

  // ══════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════════
  return (
    <div>
      {/* Stats */}
      {statsLoading ? <StatCardsSkeleton /> : renderStatCards()}

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16, borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }} bodyStyle={{ padding: '12px 16px' }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Search descriptions..."
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Action Type"
              value={actionTypeFilter}
              onChange={setActionTypeFilter}
              allowClear
              loading={actionTypesLoading}
              style={{ width: '100%' }}
            >
              {actionTypes.map((t) => {
                const cfg = getActionConfig(t.key);
                return (
                  <Option key={t.key} value={t.key}>
                    <Space size={4}>{cfg.icon}<span>{t.label}</span></Space>
                  </Option>
                );
              })}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={7}>
            <RangePicker value={dateRange} onChange={setDateRange} style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space>
              <Button icon={<SearchOutlined />} type="primary" onClick={handleSearch}>Search</Button>
              <Tooltip title="Clear Filters"><Button icon={<ClearOutlined />} onClick={handleClearFilters} /></Tooltip>
              <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={handleRefresh} /></Tooltip>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Main Table */}
      <Card
        size="small"
        title={
          <Space>
            <AuditOutlined style={{ color: '#0036b0' }} />
            <span style={{ fontWeight: 600 }}>Activity Log</span>
            <Badge count={pagination.total} showZero style={{ backgroundColor: '#0036b0' }} overflowCount={9999} />
          </Space>
        }
        style={{ borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
        bodyStyle={{ padding: loading && logs.length === 0 ? '12px 16px' : 0 }}
      >
        {loading && logs.length === 0 ? (
          <LogsTableSkeleton />
        ) : (
        <Table
          dataSource={logs}
          columns={columns}
          loading={loading && logs.length > 0}
          rowKey={(record) => record.auditId || record.id || Math.random()}
          size="small"
          scroll={{ x: 1000 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} logs`,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, pageSize) => fetchLogs(page, pageSize),
          }}
        />
        )}
      </Card>

      {/* ── Detail Modal ──────────────────────────────────────────────────────── */}
      <Modal
        title={<Space><EyeOutlined style={{ color: '#1890ff' }} /><span>Activity Detail</span></Space>}
        open={detailModal.visible}
        onCancel={() => setDetailModal({ visible: false, log: null })}
        footer={[<Button key="close" onClick={() => setDetailModal({ visible: false, log: null })}>Close</Button>]}
        width={windowWidth < 576 ? '100%' : windowWidth < 768 ? '92vw' : 700}
        style={windowWidth < 576 ? { top: 0, maxWidth: '100vw', margin: 0, padding: 0, borderRadius: 0 } : {}}
        styles={{ body: { padding: windowWidth < 576 ? '12px' : '16px 24px', maxHeight: '80vh', overflowY: 'auto' } }}
      >
        {detailModal.log && (
          <>
            {/* Mobile card-style layout */}
            {windowWidth < 576 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Action */}
                <div style={{ background: '#fafafa', borderRadius: 8, padding: '10px 14px' }}>
                  <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Action</Text>
                  <div style={{ marginTop: 6 }}>
                    {(() => { const cfg = getActionConfig(detailModal.log.actionType); return <Tag icon={cfg.icon} color={cfg.color} style={{ fontSize: 13, padding: '4px 10px' }}>{detailModal.log.actionLabel || cfg.label}</Tag>; })()}
                  </div>
                </div>

                {/* Performed By + Target */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div style={{ background: '#fafafa', borderRadius: 8, padding: '10px 14px' }}>
                    <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Performed By</Text>
                    <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#1890ff,#722ed1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
                        {(detailModal.log.performedBy?.name || 'S').charAt(0).toUpperCase()}
                      </div>
                      <Text strong style={{ fontSize: 13, wordBreak: 'break-word' }}>{detailModal.log.performedBy?.name || 'System'}</Text>
                    </div>
                  </div>
                  <div style={{ background: '#fafafa', borderRadius: 8, padding: '10px 14px' }}>
                    <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Target User</Text>
                    <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#52c41a,#13c2c2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
                        {(detailModal.log.target?.name || 'N').charAt(0).toUpperCase()}
                      </div>
                      <Text strong style={{ fontSize: 13, wordBreak: 'break-word' }}>{detailModal.log.target?.name || 'N/A'}</Text>
                    </div>
                  </div>
                </div>

                {/* Timestamp */}
                <div style={{ background: '#fafafa', borderRadius: 8, padding: '10px 14px' }}>
                  <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Timestamp</Text>
                  <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ClockCircleOutlined style={{ color: '#8c8c8c' }} />
                    <Text style={{ fontSize: 13 }}>{new Date(detailModal.log.createdAt).toLocaleString()}</Text>
                  </div>
                </div>

                {/* Description */}
                <div style={{ background: '#fafafa', borderRadius: 8, padding: '10px 14px' }}>
                  <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Description</Text>
                  <div style={{ marginTop: 6 }}>
                    <Text style={{ fontSize: 13, lineHeight: '1.5' }}>{detailModal.log.actionDescription}</Text>
                  </div>
                </div>

                {/* IP + User Agent */}
                {(detailModal.log.ipAddress || detailModal.log.userAgent) && (
                  <div style={{ display: 'grid', gridTemplateColumns: detailModal.log.ipAddress && detailModal.log.userAgent ? '1fr 1fr' : '1fr', gap: 10 }}>
                    {detailModal.log.ipAddress && (
                      <div style={{ background: '#fafafa', borderRadius: 8, padding: '10px 14px' }}>
                        <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>IP Address</Text>
                        <div style={{ marginTop: 6, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                          <GlobalOutlined style={{ color: '#8c8c8c', marginTop: 2, flexShrink: 0 }} />
                          <Text style={{ fontSize: 12, wordBreak: 'break-all', lineHeight: '1.5' }}>{detailModal.log.ipAddress}</Text>
                        </div>
                      </div>
                    )}
                    {detailModal.log.userAgent && (
                      <div style={{ background: '#fafafa', borderRadius: 8, padding: '10px 14px' }}>
                        <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>User Agent</Text>
                        <div style={{ marginTop: 6, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                          <LaptopOutlined style={{ color: '#8c8c8c', marginTop: 2, flexShrink: 0 }} />
                          <Tooltip title={detailModal.log.userAgent}>
                            <Text style={{ fontSize: 11, wordBreak: 'break-all', lineHeight: '1.5', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{detailModal.log.userAgent}</Text>
                          </Tooltip>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small" style={{ marginBottom: 16 }}>
                <Descriptions.Item label="Action" span={2}>
                  {(() => { const cfg = getActionConfig(detailModal.log.actionType); return <Tag icon={cfg.icon} color={cfg.color}>{detailModal.log.actionLabel || cfg.label}</Tag>; })()}
                </Descriptions.Item>
                <Descriptions.Item label="Performed By">
                  <Space size={4}><UserOutlined /><span>{detailModal.log.performedBy?.name || 'System'}</span></Space>
                </Descriptions.Item>
                <Descriptions.Item label="Target User">
                  <Space size={4}><TeamOutlined /><span>{detailModal.log.target?.name || 'N/A'}</span></Space>
                </Descriptions.Item>
                <Descriptions.Item label="Timestamp" span={2}>
                  <Space size={4}><ClockCircleOutlined /><span>{new Date(detailModal.log.createdAt).toLocaleString()}</span></Space>
                </Descriptions.Item>
                <Descriptions.Item label="Description" span={2}>{detailModal.log.actionDescription}</Descriptions.Item>
                {detailModal.log.ipAddress && (
                  <Descriptions.Item label="IP Address">
                    <Space size={4}><GlobalOutlined /><span style={{ wordBreak: 'break-all' }}>{detailModal.log.ipAddress}</span></Space>
                  </Descriptions.Item>
                )}
                {detailModal.log.userAgent && (
                  <Descriptions.Item label="User Agent" span={detailModal.log.ipAddress ? 1 : 2}>
                    <Tooltip title={detailModal.log.userAgent}>
                      <Space size={4}><LaptopOutlined /><Text ellipsis style={{ maxWidth: 200 }}>{detailModal.log.userAgent}</Text></Space>
                    </Tooltip>
                  </Descriptions.Item>
                )}
              </Descriptions>
            )}
            <Divider style={{ margin: '12px 0' }}>Change Details</Divider>
            {renderChangeDiff(detailModal.log.oldValue, detailModal.log.newValue)}
          </>
        )}
      </Modal>

      {/* ── User History Modal ────────────────────────────────────────────────── */}
      <Modal
        title={<Space><HistoryOutlined style={{ color: '#52c41a' }} /><span>History for {userHistoryModal.name}</span></Space>}
        open={userHistoryModal.visible}
        onCancel={() => setUserHistoryModal({ visible: false, data: [], loading: false, name: '' })}
        footer={[<Button key="close" onClick={() => setUserHistoryModal({ visible: false, data: [], loading: false, name: '' })}>Close</Button>]}
        width={windowWidth < 576 ? '95vw' : 650}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        <Spin spinning={userHistoryModal.loading}>{renderTimeline(userHistoryModal.data)}</Spin>
      </Modal>

      {/* ── Admin Actions Modal ───────────────────────────────────────────────── */}
      <Modal
        title={<Space><SafetyOutlined style={{ color: '#722ed1' }} /><span>Actions by {adminActionsModal.name}</span></Space>}
        open={adminActionsModal.visible}
        onCancel={() => setAdminActionsModal({ visible: false, data: [], loading: false, name: '' })}
        footer={[<Button key="close" onClick={() => setAdminActionsModal({ visible: false, data: [], loading: false, name: '' })}>Close</Button>]}
        width={windowWidth < 576 ? '95vw' : 650}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        <Spin spinning={adminActionsModal.loading}>{renderTimeline(adminActionsModal.data)}</Spin>
      </Modal>

      {/* ── Role History Modal ────────────────────────────────────────────────── */}
      <Modal
        title={<Space><SwapOutlined style={{ color: '#fa8c16' }} /><span>Role Changes for {roleHistoryModal.name}</span></Space>}
        open={roleHistoryModal.visible}
        onCancel={() => setRoleHistoryModal({ visible: false, data: [], loading: false, name: '' })}
        footer={[<Button key="close" onClick={() => setRoleHistoryModal({ visible: false, data: [], loading: false, name: '' })}>Close</Button>]}
        width={windowWidth < 576 ? '95vw' : 650}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        <Spin spinning={roleHistoryModal.loading}>
          {roleHistoryModal.data.length === 0 && !roleHistoryModal.loading ? (
            <Empty description="No role changes found" />
          ) : (
            <Timeline style={{ marginTop: 16 }}>
              {roleHistoryModal.data.map((item, i) => (
                <Timeline.Item key={item.auditId || i} color="orange" dot={<SwapOutlined />}>
                  <div style={{ marginBottom: 4 }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>{new Date(item.createdAt).toLocaleString()}</Text>
                  </div>
                  <Text style={{ fontSize: 13 }}>{item.actionDescription}</Text>
                  {renderChangeDiff(item.oldValue, item.newValue)}
                  {item.performedBy && (
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>Changed by: {item.performedBy.name || 'System'}</Text>
                    </div>
                  )}
                </Timeline.Item>
              ))}
            </Timeline>
          )}
        </Spin>
      </Modal>
    </div>
  );
};

export default ClientAuditLogs;
